import { useEffect, useState } from 'react';
import { SkuT } from '@lambda/apis/src/catalog-v2/types';
import { logger } from '@/lib/logger';

const useSelectedSku = (skuId: string | null, initialSku: SkuT | null) => {
  const [sku, setSku] = useState<SkuT | null>(initialSku);
  const [isFetchingSku, setIsFetchingSku] = useState(false);

  useEffect(() => {
    if (!skuId) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    let timer: NodeJS.Timeout | null = null;

    const fetchSkuById = async () => {
      if (sku?.id === skuId) {
        return;
      }

      // Delay the loading state by 150ms to avoid flickering for fast requests
      timer = setTimeout(() => {
        if (!signal.aborted) {
          setIsFetchingSku(true);
        }
      }, 150);

      try {
        const res = await fetch(`/api/catalog-v2/skus/${skuId}`, { signal });

        if (!res.ok) {
          return;
        }

        const data: SkuT = await res.json();

        if (!signal.aborted) {
          setSku(data);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          logger.error({ error, skuId }, 'Error fetching SKU by ID');
        }
      } finally {
        // Always clear timer and stop loading
        if (timer) {
          clearTimeout(timer);
        }

        if (!signal.aborted) {
          setIsFetchingSku(false);
        }
      }
    };

    fetchSkuById();

    // eslint-disable-next-line consistent-return
    return () => {
      controller.abort();

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [skuId, sku?.id]);

  return { sku, isFetchingSku };
};

export default useSelectedSku;
